import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Chip, IconButton, InputAdornment, MenuItem,
  Pagination, Select, Skeleton, Stack, TextField, Tooltip, Typography
} from '@mui/material';
import Swal from 'sweetalert2';
import Icon from '../../../components/icon';
import api from '../../../utils/axiosConfig';
import { ProsesProduksi } from './types';
import './proses-produksi.scss';

const getRows = (payload: any): ProsesProduksi[] => {
  const data = payload?.data?.data ?? payload?.data ?? payload;
  if (Array.isArray(data)) return data;
  return data?.data || data?.items || data?.rows || [];
};

const errorMessage = (error: any) =>
  error?.response?.data?.message || error?.message || 'Data proses produksi gagal dimuat.';

const rupiah = new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 });

export default function ProsesProduksiIndex() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ProsesProduksi[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | string | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const perPage = 8;

  const loadData = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/production/proses-borongan/list', { signal });
      if (data?.success === false) throw new Error(data.message || 'Data gagal dimuat.');
      setRows(getRows(data));
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') setError(errorMessage(err));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData]);

  const filtered = useMemo(() => rows.filter(row => {
    const keyword = search.toLowerCase();
    const matchText = `${row.nama_proses} ${row.keterangan || ''}`.toLowerCase().includes(keyword);
    const matchStatus = status === 'all' || String(Boolean(row.is_aktif)) === status;
    return matchText && matchStatus;
  }), [rows, search, status]);
  const pages = Math.max(1, Math.ceil(filtered.length / perPage));
  const visibleRows = filtered.slice((page - 1) * perPage, page * perPage);

  useEffect(() => setPage(1), [search, status]);
  useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);

  const remove = async (row: ProsesProduksi) => {
    const confirmation = await Swal.fire({
      icon: 'warning', title: 'Hapus proses?',
      text: `${row.nama_proses} akan dihapus permanen.`,
      showCancelButton: true, confirmButtonText: 'Ya, hapus', cancelButtonText: 'Batal',
      confirmButtonColor: '#d32f2f', reverseButtons: true,
    });
    if (!confirmation.isConfirmed || row.id == null) return;
    setDeletingId(row.id);
    try {
      const { data } = await api.delete(`/production/proses-borongan/delete/${row.id}`);
      if (data?.success === false) throw new Error(data.message || 'Data gagal dihapus.');
      setRows(current => current.filter(item => item.id !== row.id));
      Swal.fire({ icon: 'success', title: 'Terhapus', text: data?.message || 'Proses produksi berhasil dihapus.', timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Gagal menghapus', text: errorMessage(err) });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="proses-page">
      <Box className="proses-heading">
        <Box><Typography variant="h4" fontWeight={750}>Proses Produksi</Typography><Typography color="text.secondary">Kelola alur kerja dan harga borongan setiap step.</Typography></Box>
        <Button size="large" variant="contained" startIcon={<Icon icon="mdi:plus" />} onClick={() => navigate('/mkdd/proses-produksi/add')}>Tambah proses</Button>
      </Box>

      <Card className="proses-card proses-list-card">
        <Box className="proses-toolbar">
          <TextField size="small" placeholder="Cari nama proses..." value={search} onChange={e => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Icon icon="mdi:magnify" /></InputAdornment> }} />
          <Select size="small" value={status} onChange={e => setStatus(e.target.value)}>
            <MenuItem value="all">Semua status</MenuItem><MenuItem value="true">Aktif</MenuItem><MenuItem value="false">Nonaktif</MenuItem>
          </Select>
          <Tooltip title="Muat ulang"><span><IconButton disabled={loading} onClick={() => loadData()}><Icon icon="mdi:refresh" /></IconButton></span></Tooltip>
          <Typography variant="body2" color="text.secondary" ml="auto">{filtered.length} proses</Typography>
        </Box>

        {error && <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => loadData()}>Coba lagi</Button>} sx={{ m: 2 }}>{error}</Alert>}
        <Box className="proses-table-wrap">
          <table className="proses-table">
            <thead><tr><th>Proses</th><th>Step produksi</th><th>Total borongan</th><th>Status</th><th aria-label="Aksi" /></tr></thead>
            <tbody>
              {loading ? Array.from({ length: 5 }).map((_, index) => <tr key={index}>{Array.from({ length: 5 }).map((__, cell) => <td key={cell}><Skeleton height={34} /></td>)}</tr>) :
                visibleRows.map(row => {
                  const steps = row.steps || [];
                  const total = steps.reduce((sum, step) => sum + Number(step.harga_borongan || 0), 0);
                  return <tr key={row.id || row.nama_proses}>
                    <td><Typography fontWeight={700}>{row.nama_proses}</Typography>{row.keterangan && <Typography variant="caption" color="text.secondary">{row.keterangan}</Typography>}</td>
                    <td><Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">{steps.slice(0, 3).map((step, index) => <Chip key={`${step.nama_step}-${index}`} size="small" label={`${step.urutan || index + 1}. ${step.nama_step}`} variant="outlined" />)}{steps.length > 3 && <Chip size="small" label={`+${steps.length - 3}`} />}{!steps.length && <Typography variant="body2" color="text.secondary">Belum ada step</Typography>}</Stack></td>
                    <td><Typography fontWeight={650}>{rupiah.format(total)}</Typography><Typography variant="caption" color="text.secondary">{steps.length} step</Typography></td>
                    <td><Chip size="small" color={row.is_aktif ? 'success' : 'default'} label={row.is_aktif ? 'Aktif' : 'Nonaktif'} /></td>
                    <td><Box display="flex" justifyContent="flex-end"><Tooltip title="Edit"><IconButton color="primary" onClick={() => navigate(`/mkdd/proses-produksi/edit/${row.id}`)}><Icon icon="mdi:pencil-outline" /></IconButton></Tooltip><Tooltip title="Hapus"><span><IconButton color="error" disabled={deletingId === row.id} onClick={() => remove(row)}><Icon icon={deletingId === row.id ? 'mdi:loading' : 'mdi:trash-can-outline'} /></IconButton></span></Tooltip></Box></td>
                  </tr>;
                })}
            </tbody>
          </table>
          {!loading && !error && visibleRows.length === 0 && <Box className="empty-state"><Icon icon="mdi:clipboard-text-search-outline" fontSize={50} /><Typography variant="h6">Data tidak ditemukan</Typography><Typography color="text.secondary">Ubah kata pencarian atau tambahkan proses baru.</Typography></Box>}
        </Box>
        {!loading && filtered.length > perPage && <Box display="flex" justifyContent="flex-end" p={2}><Pagination count={pages} page={page} onChange={(_, value) => setPage(value)} color="primary" /></Box>}
      </Card>
    </div>
  );
}
